/**
 * purchase-request.ts
 * "Solicitar compra" — manual trigger that creates a compras.requisition
 * (real, user-supplied items) linked to a service order, at any point in
 * its lifecycle.
 *
 * Deliberately a LIGHTWEIGHT item shape (description/quantity/unit + optional
 * needDate/notes) — not the full compras RequisitionItemSchema (which also
 * carries deliveryLocationId/costCenterId/usageType/suggestedSupplierId, gated
 * by tenant_requisition_config). Those conditional fields stay editable later
 * via the full Compras item grid; this endpoint is the quick "I need to buy
 * X units of Y for this order" action, not the formal requisition-editing UI.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// PurchaseRequestItemSchema
// ---------------------------------------------------------------------------

export const PurchaseRequestItemSchema = z.object({
  description: z.string().min(1).max(1000),
  quantity: z.coerce.number().positive(),
  unit: z.string().min(1).max(32),
  needDate: z.string().datetime({ offset: true }).optional(),
  notes: z.string().max(2000).optional(),
  /**
   * fix/rental-demo ÍTEM 3 — set when the item was picked from the compras
   * item catalog buscador (same one the Solicitud de Adquisición uses),
   * instead of typed free-text. Lets the backend resolve category/unit for
   * real from item_master. Optional — a manually typed item omits it.
   */
  itemMasterId: z.string().uuid().optional(),
  /**
   * 2026-09-04-requi-solicitud-vehiculo (design D9 amendment / compras-item-
   * asset-link AL-4) — declares which asset (vehicle/equipment) this item is
   * for, forwarded unchanged into `ManualRequisitionItemInput.assetId` and,
   * from there, into `CreateCaseItemInput.assetId` — the SAME `requiresAsset`
   * gate the compras manual form's items are subject to (AL-2). Optional:
   * required only when the item's resolved rubro has `requiresAsset = true`,
   * enforced server-side, not by this schema.
   */
  assetId: z.string().uuid().optional(),
});

export type PurchaseRequestItem = z.infer<typeof PurchaseRequestItemSchema>;

// ---------------------------------------------------------------------------
// PurchaseRequestCreateSchema — POST /servicios/orders/:id/purchase-requests
// ---------------------------------------------------------------------------

export const PurchaseRequestCreateSchema = z
  .object({
    /**
     * fix/unify-solicitar-compra-detalle — optional now. REQUI's own
     * Solicitud de Adquisición never asks for a title/description either
     * (openspec/requi/alta-solicitud.md, 2026-08-12 decision) — when omitted,
     * ComprasLinkRequisitionAdapter lets createCaseInTx derive one the same
     * way REQUI's manual path already does. Still accepted for a caller that
     * wants to set it explicitly.
     */
    description: z.string().min(1).max(1000).optional(),
    priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
    neededBy: z.string().datetime({ offset: true }).optional(),
    notes: z.string().max(2000).optional(),
    /** fix/unify-solicitar-compra-detalle — matches REQUI's "Detalle" fields. */
    costCenterId: z.string().uuid().optional(),
    deliveryLocationId: z.string().uuid().optional(),
    reference: z.string().max(256).optional(),
    items: z.array(PurchaseRequestItemSchema).min(1, 'At least one item is required'),
  })
  .strict();

export type PurchaseRequestCreate = z.infer<typeof PurchaseRequestCreateSchema>;

// ---------------------------------------------------------------------------
// PurchaseRequestResponseSchema
// ---------------------------------------------------------------------------

export const PurchaseRequestResponseSchema = z.object({
  requisitionId: z.string().uuid(),
  requisitionNumber: z.string(),
});

export type PurchaseRequestResponse = z.infer<typeof PurchaseRequestResponseSchema>;
